import type { Breadcrumb } from '@sentry/react';

// The one clean-up for what the app hands to Sentry (src/sentry.ts): no magic-link login token
// (PROD-2197) and no Community search text (PROD-2238) may reach it. Taking the token out of the
// address bar is not enough: the browser's navigation-timing entry keeps the URL the page was
// loaded with and Sentry copies it into replay and transaction spans. The search text is never in
// the page address; it is in the query of every search request Sentry records.

// `token=` anywhere; `q=` (doula search) and `search=` (post search) only as query parameters,
// plain or URL-encoded. Stops at quotes, so it is also safe on JSON text. No lookbehind: Safari
// before 16.4 can't parse one and the whole app would fail to load.
const FILTERED_PARAM = /(token|[?&](?:q|search)|%3F(?:q|search)|%26(?:q|search))(=|%3D)[^&#"\\\s]*/gi;

export const scrubSentryText = (text: string) => text.replace(FILTERED_PARAM, '$1$2[Filtered]');

const isPlainObject = (value: object) => {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/**
 * A copy of `value` with every string cleaned. Only arrays and plain objects are copied; anything
 * else (the SDK's scopes and replay, errors, dates) is kept by reference, so live SDK objects are
 * never walked. Cycles map to their copies. The input is never changed. Throws when a value can't
 * be read; the hooks turn that into "send nothing".
 */
export function scrubStrings<T>(value: T): T {
  const copies = new WeakMap<object, unknown>();

  const walk = (item: unknown): unknown => {
    if (typeof item === 'string') return scrubSentryText(item);
    if (!item || typeof item !== 'object') return item;
    if (copies.has(item)) return copies.get(item);

    if (Array.isArray(item)) {
      const copy: unknown[] = [];
      copies.set(item, copy);
      item.forEach((entry) => copy.push(walk(entry)));
      return copy;
    }
    if (!isPlainObject(item)) return item;

    const copy: Record<string, unknown> = {};
    copies.set(item, copy);
    Object.entries(item).forEach(([key, entry]) => {
      copy[key] = walk(entry);
    });
    return copy;
  };

  return walk(value) as T;
}

/**
 * An event or transaction with every string cleaned. `sdkProcessingMetadata` holds live, cyclic SDK
 * objects and is never sent, so it goes back as it is — except its dynamic sampling context, which
 * Sentry copies into the envelope header before dropping the rest.
 */
export function scrubEvent<T extends { sdkProcessingMetadata?: Record<string, unknown> }>(event: T): T {
  const { sdkProcessingMetadata: metadata, ...rest } = event;
  const cleaned = scrubStrings(rest);
  if (!metadata) return cleaned as T;

  const sdkProcessingMetadata =
    'dynamicSamplingContext' in metadata
      ? { ...metadata, dynamicSamplingContext: scrubStrings(metadata.dynamicSamplingContext) }
      : metadata;
  return { ...cleaned, sdkProcessingMetadata } as T;
}

const BREADCRUMB_URL_FIELDS = ['url', 'from', 'to'] as const;

/**
 * A breadcrumb with its address fields cleaned: `message`, and `data.url` / `from` / `to`. Other data
 * (console arguments are live app objects) stays as it is; Sentry normalises it later and the event
 * or replay hook cleans it then.
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const { message, data } = breadcrumb;
  const cleaned = { ...breadcrumb };
  if (typeof message === 'string') cleaned.message = scrubSentryText(message);
  if (data) {
    const cleanedData = { ...data };
    BREADCRUMB_URL_FIELDS.forEach((field) => {
      if (typeof data[field] === 'string') cleanedData[field] = scrubSentryText(data[field]);
    });
    cleaned.data = cleanedData;
  }
  return cleaned;
}
